// The one place a session ends.
//
// A bulk `tokenVersion` bump (GLBA-13584) kills every session at once, so on an
// analytics page ten or more requests fail in the same tick. Without a latch that
// is ten redirects and a toast storm, which is why `endSession` runs once and then
// does nothing.
//
// Two deliberate non-imports: the router (router/index.js imports the store, whose
// actions import the api client, which imports this — a router import here closes
// that ring) and the store (same ring, one link shorter). Nothing is lost: ending a
// session ends with a document navigation, which needs neither, and it takes the
// in-memory Vuex state with it.

// Cleared on teardown. Deliberately absent: `currentFiatKey`, which app.vue reacts to
// cross-tab with a location.reload(), and `to.fullPath` / `lastRoute`, which are how
// the user gets back to where they were after signing in again.
const AUTH_KEYS = ['token', 'refreshToken', 'id']

// Cross-tab teardown travels on its own key. It cannot travel on `token`: a healthy
// refresh writes that key twice (remove, then set), so sibling tabs would log
// themselves out in the middle of a perfectly good refresh.
export const SESSION_END_KEY = 'gl:session-ended'

export const SESSION_END_REASONS = {
  // The server no longer accepts our tokens — expiry, or a bulk invalidation.
  expired: 'session_expired',
  // The account is refused its own profile: disabled, or stripped of its access.
  denied: 'access_denied',
  // We could not reach the auth service at all. Different message, same exit.
  unavailable: 'auth_unavailable',
}

let ending = false

// A live token means any end-of-session signal still in storage is stale. Left behind
// it outlives the session it described, and a sibling tab removing it broadcasts a
// `null` value, which the listener ignores by design.
export function clearSessionEndSignal() {
  localStorage.removeItem(SESSION_END_KEY)
}

// Drops the credentials without navigating anywhere. Safe to call from a place that
// must not move the user — a voluntary logout, a test, a teardown that continues.
export function clearAuthSession() {
  AUTH_KEYS.forEach(key => localStorage.removeItem(key))
}

// Ends the session for good: credentials gone, siblings told, document replaced by
// the login page carrying the reason. Idempotent by design — see the note above.
export function endSession(reason, { broadcast = true } = {}) {
  if (ending) return
  ending = true

  clearAuthSession()

  if (broadcast) {
    // A receiver must not re-broadcast, or two tabs ping-pong forever.
    try {
      localStorage.setItem(SESSION_END_KEY, `${reason}:${Date.now()}`)
    } catch (error) {
      console.error(error)
    }
  }

  window.location.assign(`/login?reason=${encodeURIComponent(reason)}`)
}

// Test seam: the latch lives for the life of the document, which is exactly right in
// a browser and useless in jest.
export function resetSessionLatch() {
  ending = false
}
